import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Quote } from "lucide-react";
import { LOGO_URL, FOUNDER_PORTRAIT } from "../lib/data";
import { useContent } from "../context/ContentContext";
import Footer from "../components/Footer";
import Markdown from "../components/Markdown";

const DEFAULT_STORY = `Straßenwert ist aus einer echten Freundschaft entstanden – aus Leidenschaft für Musik, gemeinsamen Visionen und dem Willen, etwas Eigenes aufzubauen.

Schon bevor Straßenwert überhaupt existiert hat, war klar, dass Nono für die Musik lebt. Er macht seit vielen Jahren Musik, hat immer vor mir gerappt und ich habe von Anfang an gesehen, welches Potenzial in ihm steckt. Während er am Mikrofon stand, kam ich eher aus einer anderen Richtung. Durch meine bisherigen beruflichen Erfahrungen wusste ich, wie man Projekte aufbaut, organisiert und Menschen zusammenbringt. Genau dadurch entstand irgendwann der Gedanke: Warum nicht aus Talent, Leidenschaft und Vision etwas Eigenes machen?

So begann die Idee hinter Straßenwert.

Ich, **Maurizio Rogait**, wollte schon immer etwas mit Musik machen, auch wenn ich selbst nie der klassische Rapper war. Stattdessen habe ich angefangen, mich immer stärker mit der kreativen und organisatorischen Seite auseinanderzusetzen – Management, Aufbau, Visionen und später auch Produktion. Irgendwann kam dann der Punkt, an dem ich gesagt habe: Wenn wir das wirklich ernst meinen, dann bringe ich mir Producing selbst bei und wir ziehen das gemeinsam durch.

Genau das ist passiert.

Während Nono weiter Musik gemacht und seinen eigenen Sound entwickelt hat, habe ich angefangen zu produzieren, Konzepte aufzubauen und Straßenwert Schritt für Schritt wachsen zu lassen. Aus langen Nächten, Studio-Sessions, Gesprächen und Ideen wurde nach und nach mehr als nur ein gemeinsames Projekt.

Straßenwert steht heute für **Authentizität, Loyalität und echte Leidenschaft**. Nicht für eine Rolle, die man spielt, sondern für Menschen, die ihren eigenen Weg gehen und gemeinsam etwas erschaffen, hinter dem sie wirklich stehen.

Was als Vision unter besten Freunden begonnen hat, entwickelt sich jeden Tag weiter – mit Musik, Kreativität und einer klaren Mission: etwas Echtes zu hinterlassen.`;

export default function GeschichtePage() {
  const { content } = useContent();
  const story = content?.story || {};
  const body = story.body || DEFAULT_STORY;

  return (
    <div className="min-h-screen bg-[#050505] text-white grain">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#050505]/85 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 md:py-4 grid grid-cols-[1fr_auto_1fr] items-center gap-8">
          <Link
            to="/"
            data-testid="story-back"
            className="inline-flex items-center gap-2 text-[10px] md:text-[11px] font-semibold tracking-[0.25em] uppercase text-zinc-400 hover:text-white transition-colors justify-self-start"
          >
            <ArrowLeft size={14} /> Zurück
          </Link>
          <Link to="/" className="flex items-center group shrink-0">
            <img
              src={LOGO_URL}
              alt="STRAßENWERT"
              className="h-11 md:h-13 w-auto object-contain drop-shadow-[0_0_18px_rgba(255,0,51,0.4)] group-hover:drop-shadow-[0_0_28px_rgba(255,0,51,0.7)] transition-all duration-500"
            />
          </Link>
          <div className="justify-self-end w-8 md:w-12" />
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden py-20 md:py-32 px-6 md:px-12">
        <div className="absolute top-1/3 right-1/4 w-[42rem] h-[42rem] rounded-full bg-[#ff0033]/12 blur-[160px] pointer-events-none" />
        <div className="absolute top-0 left-0 right-0 h-px hairline-red opacity-50" />

        <div className="relative max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <div className="flex items-center gap-3 mb-6 text-[10px] md:text-[11px] tracking-[0.4em] uppercase text-zinc-500 font-semibold">
              <span className="block w-10 h-px bg-[#ff0033] shadow-[0_0_10px_#ff0033]" />
              {story.eyebrow || "Die Geschichte hinter Straßenwert"}
            </div>
            <h1 className="font-display font-black uppercase tracking-tighter leading-[0.88] text-[12vw] sm:text-7xl lg:text-8xl">
              {story.title_a || "Vom Block"} <br />
              <span className="text-glow-red-strong text-white">{story.title_b || "ins Studio."}</span>
            </h1>
            {story.subtitle && (
              <p className="mt-7 max-w-2xl text-base md:text-lg text-zinc-400 leading-relaxed">
                {story.subtitle}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Portrait + Quote */}
      <section className="relative px-6 md:px-12 pb-16 md:pb-24">
        <div className="relative max-w-6xl mx-auto grid lg:grid-cols-12 gap-10 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-5"
          >
            <div className="relative aspect-[3/4] overflow-hidden border border-white/[0.08] bg-[#0A0A0A] group">
              <img
                src={story.portrait_url || FOUNDER_PORTRAIT}
                alt={story.author_name || "Maurizio Rogait — Gründer Straßenwert"}
                data-testid="story-portrait"
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-[1400ms] ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/70 via-transparent to-transparent pointer-events-none" />
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 z-10">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold mb-2">
                  {story.author_role || "Gründer"}
                </div>
                <div className="font-display font-black uppercase text-2xl md:text-3xl tracking-tight">
                  {story.author_name || "Maurizio Rogait"}
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="lg:col-span-7"
          >
            {story.quote && (
              <div className="relative border-l-2 border-[#ff0033] pl-6 md:pl-8 mb-10">
                <Quote size={28} className="text-[#ff0033] mb-4 -ml-1 opacity-60" />
                <p className="font-display font-black uppercase text-2xl md:text-4xl lg:text-5xl tracking-tighter leading-[1.05]">
                  „{story.quote}"
                </p>
                {story.quote_attribution && (
                  <div className="mt-5 text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
                    — {story.quote_attribution}
                  </div>
                )}
              </div>
            )}

            <div className="legal-body" data-testid="story-body">
              <Markdown source={body} />
            </div>

            {(story.signoff || story.author_name) && (
              <div className="mt-12 pt-8 border-t border-white/[0.06]">
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">
                  {story.signoff || "Mit Liebe & Hustle"}
                </div>
                <div className="font-display font-black uppercase text-2xl tracking-tight">
                  {story.author_name || "Maurizio Rogait"}
                </div>
              </div>
            )}

            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-3 border border-white/20 px-6 py-3.5 text-[10px] md:text-[11px] font-bold tracking-[0.3em] uppercase text-zinc-200 hover:border-[#ff0033]/60 hover:text-white transition-all"
              >
                Zur Hauptseite
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
